/**
 * ``useCartQuote`` — debounced, server-authoritative price preview for the cart.
 *
 * Re-quotes via ``POST /api/cart/quote/`` whenever the selection *or* the requested currency
 * spend changes (debounced so rapid +/- taps and field edits don't spam the server). The
 * returned ``quote`` is the single source of truth for every amount the checkout screen and
 * bottom panel show; the client never sums prices itself (ТЗ §5).
 */
import { ApiError } from "@shared/api";
import { useEffect, useState } from "react";

import { quoteCart, type CartQuote } from "./api";
import { cartSignature, toItemsPayload, type CartItems } from "./model";

const QUOTE_DEBOUNCE_MS = 250;

/** Requested currency spend (integer units) folded into the quote (ТЗ §5). */
export interface CartSpend {
  readonly starGlitterSpend?: number;
  readonly primogemSpend?: number;
}

export interface CartQuoteState {
  readonly quote: CartQuote | null;
  readonly error: ApiError | null;
  readonly isLoading: boolean;
}

export function useCartQuote(
  items: CartItems,
  spend: CartSpend = {},
): CartQuoteState {
  const [quote, setQuote] = useState<CartQuote | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const starGlitterSpend = spend.starGlitterSpend ?? 0;
  const primogemSpend = spend.primogemSpend ?? 0;

  // Re-run only when the selection or the requested spend actually changes.
  const signature = `${cartSignature(items)}|${starGlitterSpend}|${primogemSpend}`;

  useEffect(() => {
    if (items.size === 0) {
      setQuote(null);
      setError(null);
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    const payload = toItemsPayload(items);
    const handle = setTimeout(() => {
      quoteCart({
        items: payload,
        star_glitter_spend: starGlitterSpend,
        primogem_spend: primogemSpend,
      })
        .then((next) => {
          if (cancelled) {
            return;
          }
          setQuote(next);
          setError(null);
        })
        .catch((err: unknown) => {
          if (cancelled) {
            return;
          }
          setError(
            err instanceof ApiError ? err : new ApiError(0, "Network error", err),
          );
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }, QUOTE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // ``signature`` captures the meaningful change; ``items`` identity is read inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return { quote, error, isLoading };
}
